import { useState, useEffect } from 'react';
import axios from 'axios';
import { Edit, Trash2, Plus, BookOpen, CalendarClock } from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmDialog from './ConfirmDialog';
import Modal from './Modal';
import AdminDateInput from './AdminDateInput';
import ContentImageInput from './ContentImageInput';

interface Post {
  id: number;
  title: string;
  content: string;
  imageUrl: string | null;
  createdAt: string;
  expiresOn: string | null;
  author: {
    firstName: string;
    lastName: string;
  };
}

const getParisDate = () => {
  const parts = new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date())

  const year = parts.find(part => part.type === 'year')?.value
  const month = parts.find(part => part.type === 'month')?.value
  const day = parts.find(part => part.type === 'day')?.value

  return year && month && day ? `${year}-${month}-${day}` : ''
}

const formatExpirationDate = (expiresOn: string) => {
  const [year, month, day] = expiresOn.split('-').map(Number)
  return new Date(year, month - 1, day).toLocaleDateString('fr-FR')
}

const getExpirationStatus = (expiresOn: string | null) => {
  if (!expiresOn) return { label: 'Sans expiration', tone: 'none' }

  const today = getParisDate()
  if (expiresOn < today) return { label: 'Expirée', tone: 'expired' }
  if (expiresOn === today) return { label: "Expire aujourd'hui", tone: 'today' }
  return { label: 'Active', tone: 'active' }
}

const PostsAdminModule = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    imageUrl: '',
    expiresOn: '',
  });
  const [postToDelete, setPostToDelete] = useState<Post | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/posts/admin');
      setPosts(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des posts');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (post: Post) => {
    setEditingPost(post);
    setFormData({ title: post.title, content: post.content, imageUrl: post.imageUrl ?? '', expiresOn: post.expiresOn ?? '' });
    setShowForm(true);
  };

  const handleDelete = async () => {
    if (!postToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`/api/posts/${postToDelete.id}`);
      toast.success('Post supprimé');
      setPostToDelete(null);
      await fetchPosts();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingPost(null);
    setFormData({ title: '', content: '', imageUrl: '', expiresOn: '' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        title: formData.title,
        content: formData.content,
        imageUrl: formData.imageUrl || null,
        expiresOn: formData.expiresOn || null,
      }

      if (editingPost) {
        await axios.put(`/api/posts/${editingPost.id}`, payload);
        toast.success('Post modifié');
      } else {
        await axios.post('/api/posts', payload);
        toast.success('Post créé');
      }
      setShowForm(false);
      setEditingPost(null);
      setFormData({ title: '', content: '', imageUrl: '', expiresOn: '' });
      fetchPosts();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  return (
      <div className="space-y-6">
        <div className="admin-module-toolbar">
          <div>
            <p className="eyebrow !mb-1">Communication</p>
            <h3 className="text-xl font-extrabold text-gray-900">Publications existantes</h3>
          </div>
          <button
            className="btn-primary"
            onClick={() => {
              setShowForm(true);
              setEditingPost(null);
              setFormData({ title: '', content: '', imageUrl: '', expiresOn: '' });
            }}
          >
            <Plus className="w-4 h-4" /> Nouveau post
          </button>
        </div>

        <Modal
          isOpen={showForm}
          onClose={closeForm}
          title={editingPost ? 'Modifier la publication' : 'Nouvelle publication'}
          eyebrow="Actualités du club"
          size="md"
          footer={(
            <>
              <button type="button" className="btn-secondary" onClick={closeForm}>Annuler</button>
              <button type="submit" form="post-admin-form" className="btn-primary">
                {editingPost ? 'Mettre à jour' : 'Publier'}
              </button>
            </>
          )}
        >
            <form id="post-admin-form" onSubmit={handleSubmit} className="space-y-5">
              <div className="modal-section flex items-start gap-3 p-4">
                <span className="card-icon" aria-hidden="true"><BookOpen /></span>
                <p className="text-sm leading-6 text-gray-600">Cette publication apparaîtra dans la section Actualités de l’accueil.</p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-bold text-gray-700" htmlFor="post-title">Titre</label>
                <input
                  id="post-title"
                  type="text"
                  className="w-full border px-3 py-2"
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-bold text-gray-700" htmlFor="post-content">Contenu</label>
                <textarea
                  id="post-content"
                  className="min-h-40 w-full border px-3 py-2"
                  value={formData.content}
                  onChange={e => setFormData({ ...formData, content: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-bold text-gray-700" htmlFor="post-image">Image <span className="font-medium text-gray-500">(facultative)</span></label>
                <ContentImageInput id="post-image" value={formData.imageUrl} onChange={imageUrl => setFormData({ ...formData, imageUrl })} />
              </div>
              <div>
                <label className="mb-1 flex items-center gap-2 text-sm font-bold text-gray-700" htmlFor="post-expiration">
                  <CalendarClock className="h-4 w-4 text-primary-600" aria-hidden="true" />
                  Date d'expiration <span className="font-medium text-gray-500">(facultative)</span>
                </label>
                <AdminDateInput
                  id="post-expiration"
                  value={formData.expiresOn}
                  onChange={expiresOn => setFormData({ ...formData, expiresOn })}
                  className="w-full"
                />
                <p className="mt-2 text-xs leading-5 text-gray-500">
                  La publication restera visible pendant toute cette journée, puis sera masquée de l'accueil sans être supprimée.
                </p>
              </div>
            </form>
        </Modal>

        {/* Liste des posts */}
        <div className="card !p-0 overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : posts.length === 0 ? (
            <div className="empty-state !border-0">Aucune publication pour le moment.</div>
          ) : (
          <div className="data-table-wrap"><table className="admin-table">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Titre</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Auteur</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiration</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {posts.map(post => {
                  const expirationStatus = getExpirationStatus(post.expiresOn)

                  return <tr key={post.id}>
                    <td className="px-6 py-4 whitespace-nowrap font-medium">{post.title}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{post.author.firstName} {post.author.lastName}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{new Date(post.createdAt).toLocaleDateString('fr-FR')}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`post-expiration-status post-expiration-status-${expirationStatus.tone}`}>
                        {expirationStatus.label}
                      </span>
                      {post.expiresOn && <small className="mt-1 block text-gray-500">{formatExpirationDate(post.expiresOn)}</small>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap flex gap-2">
                      <button onClick={() => handleEdit(post)} className="icon-button" title="Modifier" aria-label={`Modifier ${post.title}`}>
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => setPostToDelete(post)} className="icon-button icon-button-danger" title="Supprimer" aria-label={`Supprimer ${post.title}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                })}
              </tbody>
            </table></div>
          )}
        </div>
        <ConfirmDialog
          isOpen={postToDelete !== null}
          title="Supprimer la publication"
          message={`La publication « ${postToDelete?.title ?? ''} » sera définitivement supprimée.`}
          confirmLabel="Supprimer"
          isBusy={deleting}
          onConfirm={handleDelete}
          onCancel={() => setPostToDelete(null)}
        />
      </div>
  );
};

export default PostsAdminModule;
